import React, { useEffect, useState } from 'react';
import { apiClient } from '../api/client';
import { PortfolioHolding } from '../types';
import { formatINR } from '../utils/currency';

const HoldingsList: React.FC = () => {
  const [holdings, setHoldings] = useState<PortfolioHolding[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchHoldings();
  }, []);

  const fetchHoldings = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getPortfolioHoldings();
      setHoldings(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load holdings');
      console.error('Error fetching holdings:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="rounded-xl border border-white/10 bg-white/5 p-6 animate-pulse">
            <div className="h-6 bg-white/10 rounded w-1/3 mb-4"></div>
            <div className="h-4 bg-white/10 rounded w-1/2"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-6">
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  if (holdings.length === 0) {
    return (
      <div className="rounded-xl border border-white/10 bg-white/5 p-12 text-center">
        <p className="text-slate-400 text-lg mb-2">No holdings yet</p>
        <p className="text-slate-500 text-sm">Add assets and record transactions to see your holdings here</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">Current Holdings</h2>
        <span className="text-sm text-slate-400">{holdings.length} position{holdings.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="space-y-3">
        {holdings.map((holding) => {
          const quantity = parseFloat(holding.quantity);
          const avgPrice = parseFloat(holding.average_buy_price);
          const currentPrice = parseFloat(holding.current_price);
          const currentValue = parseFloat(holding.current_value);
          const unrealizedGain = parseFloat(holding.unrealized_gain);
          const unrealizedGainPct = parseFloat(holding.unrealized_gain_percentage);
          const costBasis = parseFloat(holding.cost_basis);
          
          const isProfit = unrealizedGain >= 0;

          return (
            <div
              key={holding.asset_id}
              className="rounded-xl border border-white/10 bg-white/5 p-6 hover:border-white/20 transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-bold text-lg">{holding.symbol}</h3>
                  <p className="text-sm text-slate-400">{holding.name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="px-2 py-0.5 text-xs rounded bg-white/10 text-slate-300">
                      {holding.asset_type}
                    </span>
                    <span className="text-xs text-slate-500">{holding.category}</span>
                  </div>
                </div>
                <div className={`text-right ${isProfit ? 'text-green-400' : 'text-red-400'}`}>
                  <p className="text-2xl font-bold">{formatINR(currentValue)}</p>
                  <p className="text-sm">
                    {isProfit ? '+' : ''}{formatINR(unrealizedGain)} ({isProfit ? '+' : ''}{unrealizedGainPct.toFixed(2)}%)
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-white/10">
                <div>
                  <p className="text-xs text-slate-500 mb-1">Quantity</p>
                  <p className="font-semibold">{quantity.toFixed(4)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Avg Buy Price</p>
                  <p className="font-semibold">{formatINR(avgPrice)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Current Price</p>
                  <p className="font-semibold">{formatINR(currentPrice)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Invested</p>
                  <p className="font-semibold">{formatINR(costBasis)}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HoldingsList;